<script setup lang='ts'>
import { computed, watch, ref, onMounted, onBeforeUnmount } from 'vue'

import AppMenu from '~/components/app/AppMenu.vue'
import { iViewPortalDirectusUser } from '~/interfaces'
const { layoutConfig, layoutState, isSidebarActive } = useLayout()
const outsideClickListener = ref<((event: MouseEvent) => void) | null>(null)
const isDesktop = ref(false)
const themeStore = useThemeStore()
const updateViewport = () => {
	isDesktop.value = typeof window !== 'undefined' ? window.innerWidth > 991 : false
}
const isMenuExpanded = computed(() => {
	if (isDesktop.value) {
		return layoutState.overlayMenuActive.value || !layoutState.staticMenuDesktopInactive.value
	}

	return layoutState.staticMenuMobileActive.value || layoutState.overlayMenuActive.value
})

/* // in modifica per color template da nero a bianco che sfarfalla
//const coockies = useCookie('theme_colors')
const linkCss = ref('')
const getUSerSession = async () => {
	const session = await useAuth().getSession()
	console.log('default process is server: ', process.server, ' linkCss: ', linkCss,	' session user is: ', session.user)

	if (session) {
		const user = session.user as iViewPortalDirectusUser
		if (user?.customization?.theme_colors) {
			themeStore.setOptionFromUser(user.customization.theme_colors)
			linkCss.value = themeStore.link
			coockies.value = themeStore.link
			console.log('default getUSerSession linkCss.value: ', linkCss.value)
		}
	} else {
		console.log('!!!default getUSerSession linkCss.value gia valorizzato: ', linkCss)
	}
}
 getUSerSession().then(() => {
	console.log('default getUSerSession: ', themeStore.link)
}) */
onMounted(async () => {
	await useUserSessionManagement().loadUserFromSession()
	// console.log('default onMounted coockies: ', coockies.value)
	updateViewport()
	layoutState.staticMenuDesktopInactive.value = true
	window.addEventListener('resize', updateViewport)
})

// Watcher globale per sincronizzare il menu attivo con la route
const route = useRoute()
const activeMenu = useActiveMenu()
watch(() => route.path, (newPath) => {
	// Normalizza il path
	const normalizedPath = newPath.endsWith('/') && newPath !== '/' ? newPath.slice(0, -1) : newPath

	// Aggiorna il menu attivo in base al path corrente
	if (normalizedPath === '' || normalizedPath === '/') {
		// Home: reset tutto
		activeMenu.resetActiveMenu()
	} else {
		// Imposta il path attivo
		activeMenu.setActiveMenuByPath(normalizedPath)
	}
}, { immediate: true })

onBeforeUnmount(() => {
	if (typeof window === 'undefined') {
		return
	}

	window.removeEventListener('resize', updateViewport)
})

watch(isSidebarActive, (newVal) => {
	if (newVal) {
		bindOutsideClickListener()
	} else {
		unbindOutsideClickListener()
	}
})
const containerClass = computed(() => {
	return {
		'layout-theme-light': !layoutConfig.darkTheme.value,
		'layout-theme-dark': layoutConfig.darkTheme.value,
		'layout-overlay': layoutConfig.menuMode.value === 'overlay',
		'layout-static': layoutConfig.menuMode.value === 'static',
		'layout-static-inactive': layoutState.staticMenuDesktopInactive.value && layoutConfig.menuMode.value === 'static',
		'layout-overlay-active': layoutState.overlayMenuActive.value,
		'layout-mobile-active': layoutState.staticMenuMobileActive.value,
		'p-input-filled': layoutConfig.inputStyle.value === 'filled',
		'p-ripple-disabled': !layoutConfig.ripple.value
	}
})
const mainContainerStyle = computed(() => {
	const backgroundColor = themeStore.link ? (!themeStore.isDarkMode ? '#e7e7e7' : '#17212f') : '#e7e7e7'
	const marginLeft = isDesktop.value ? (isMenuExpanded.value ? '25rem' : '3rem') : '0'
	const horizontalPadding = isDesktop.value ? '2rem' : '1.5rem'

	return {
		backgroundColor,
		marginLeft,
		paddingLeft: horizontalPadding,
		paddingRight: horizontalPadding,
		transition: 'margin-left 0.3s ease'
	}
})

const bindOutsideClickListener = () => {
	if (!outsideClickListener.value) {
		outsideClickListener.value = (event: MouseEvent) => {
			if (isOutsideClicked(event)) {
				layoutState.overlayMenuActive.value = false
				layoutState.staticMenuMobileActive.value = false
				layoutState.menuHoverActive.value = false
			}
		}

		if (outsideClickListener.value) {
			document.addEventListener('click', outsideClickListener.value)
		}
	}
}

const unbindOutsideClickListener = () => {
	if (!outsideClickListener.value) {
		return
	}

	document.removeEventListener('click', outsideClickListener.value)
	outsideClickListener.value = null
}

const isOutsideClicked = (event: MouseEvent) => {
	const sidebarEl = document.querySelector('.layout-sidebar')
	const topbarEl = document.querySelector('.layout-menu-button')
	const target = event.target as Node | null

	if (!sidebarEl || !topbarEl || !target) {
		return false
	}

	return !(sidebarEl.isSameNode(target) || sidebarEl.contains(target) || topbarEl.isSameNode(target) || topbarEl.contains(target))
}
const getLogoHomeCompose = computed(() => {
	return themeStore.getLoadLogoHome
})
</script>

<template>
	<!-- <div v-if="!themeStore.link">
    <div class="p-d-flex p-jc-center p-ai-center" style="height: 100vh">
      <ProgressBar mode="indeterminate" style="height: 16px" class="border-2" />
      <ProgressSpinner />
    </div>
  </div> -->
	<div>
		<!-- Tema PrimeVue esterno (saga/vela/arya-green) -->
		<Link rel="stylesheet"
			:href="themeStore.link || 'https://cdn.jsdelivr.net/npm/primevue@3.15.0/resources/themes/saga-green/theme.min.css'" />

		<!-- Override colori Redigo - DEVE essere caricato DOPO il tema PrimeVue -->
		<Link rel="stylesheet" href="/assets/css/theme-redigo-override.css" />

		<div class="layout-wrapper layout-dark" :class=" containerClass">
			<!--v-show="getLogoHomeCompose"-->
			<app-topbar />
			<div v-if="getLogoHomeCompose" class="layout-sidebar surface-0"
				:class="isMenuExpanded ? 'w-27rem' : 'w-5rem'" style="z-index: 998; transition: width 0.3s ease;">
				<app-menu />
			</div>
			<!--      <Sidebar v-model:visible="userCompose().openMenu.value" header="Right Sidebar" position="right">
        <p>Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.</p>
      </Sidebar> -->
			<!-- :class="!themeStore.isDarkMode ? 'bg-primary-800 text-white' : 'surface-ground text-color'"

	-->
			<div class="layout-main-container py-0 pr-0" :style="mainContainerStyle">
				<!-- <img
          id="top_left_content_image"
          :src="themeStore.isDarkMode ? '/images/home_icone/home_deleganoi_transparent_dark.png' : '/images/home_icone/home_deleganoi_transparent_white.png'"
          alt="Image"
          class="fixed top-20 left-0 h-16rem"
        > -->

				<div class="layout-main" style="z-index: 1;">
					<slot />
				</div>
				<img id="bottom_right_content_image"
					:src="themeStore.isDarkMode ? '/images/home_icone/R_background_bottom_right_tema_scuro.png' : '/images/home_icone/R_background_bottom_right_tema_chiaro.png'"
					alt="Image" class="fixed border-0 right-0 bottom-0"
					style="z-index: 0; height: 300px; "><app-footer />
			</div>
			<div class="layout-mask hidden" />
		</div>
	</div>
</template>

<style lang="scss" scoped></style>
